import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useRef } from 'react';

import useUnifiedVirtualCardIssuance, {
  UnifiedVirtualCardIssuanceResponse,
} from 'hooks/requests/unifiedVirtualCardIssuance';
import useUnifiedVirtualCardIssuanceStatus, {
  UnifiedVirtualCardIssuanceStatusResponse,
} from 'hooks/requests/unifiedVirtualCardIssuanceStatus';
import useAnalytics, { Event } from 'hooks/useAnalytics';
import { selectCreditApplication } from 'state/creditApplication';
import { selectFinanceResult } from 'state/financeOffers';
import { selectTimeouts } from 'state/timeouts';
import { mergeInstantCredit } from 'state/instantCredit';
import { selectTenant } from 'state/tenant';
import useFmcHost from 'hooks/useFmcHost';
import {
  receiveVirtualCardIssuanceResult,
  VirtualCardIssuanceResultType,
} from 'fmc-sdk';

type IssuanceHook = () => () => Promise<'error' | 'success' | 'timeout'>;

const useIssuance: IssuanceHook = () => {
  const [unifiedVirtualCardIssuanceRequest] = useUnifiedVirtualCardIssuance();
  const [
    unifiedVirtualCardIssuanceStatusRequest,
  ] = useUnifiedVirtualCardIssuanceStatus();
  const { firstName, lastName, middleInitial } = useSelector(
    selectCreditApplication,
  );
  const { issuanceTimeout } = useSelector(selectTimeouts);
  const financeResult = useSelector(selectFinanceResult);
  const { sendEvents } = useAnalytics();
  const fmcDispatch = useFmcHost();

  const financingId = financeResult ? financeResult.id : null;

  const {
    enabledFeatures: { financingWallet },
  } = useSelector(selectTenant);
  const dispatch = useDispatch();

  const abortRef = useRef<NodeJS.Timeout | null>(null);
  const aborted = useCallback(() => abortRef.current === null, []);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const pollingInterval = 2000;
  const timeoutInterval = issuanceTimeout * 1000;

  const cleanup = useCallback(() => {
    if (abortRef.current) {
      clearTimeout(abortRef.current);
    }
    abortRef.current = null;
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const stillProcessing = useCallback(
    (
      result:
        | UnifiedVirtualCardIssuanceResponse
        | UnifiedVirtualCardIssuanceStatusResponse,
    ) => {
      return result.issuanceStatus === 'REQUESTED' && !aborted();
    },
    [aborted],
  );

  const handleResult = useCallback(
    (
      result:
        | UnifiedVirtualCardIssuanceResponse
        | UnifiedVirtualCardIssuanceStatusResponse,
    ) => {
      if (aborted()) {
        sendEvents([Event.IssuanceTimedOut]);
        fmcDispatch(
          receiveVirtualCardIssuanceResult({
            result: VirtualCardIssuanceResultType.Timeout,
            statusCodes: [],
          }),
        );
        return 'timeout';
      } else if (result.issuanceStatus === 'ISSUED') {
        const pan =
          result.accountNumber &&
          financingWallet &&
          financingWallet.panRetrieval
            ? result.accountNumber
            : undefined;

        dispatch(
          mergeInstantCredit({
            firstName: firstName ? firstName : undefined,
            lastName: lastName ? lastName : undefined,
            middleInitial: middleInitial ? middleInitial : undefined,
            creditLimit: result.cardCapacityValue
              ? result.cardCapacityValue / 100
              : undefined,
            accountNumber: pan,
          }),
        );
        sendEvents([Event.IssuanceSucceeded]);
        fmcDispatch(
          receiveVirtualCardIssuanceResult({
            result: VirtualCardIssuanceResultType.CardIssued,
            statusCodes: [],
          }),
        );
        return 'success';
      }
      sendEvents([Event.IssuanceFailed]);
      fmcDispatch(
        receiveVirtualCardIssuanceResult({
          result: VirtualCardIssuanceResultType.Error,
          statusCodes: [],
        }),
      );
      return 'error';
    },
    [
      dispatch,
      financingWallet,
      firstName,
      lastName,
      middleInitial,
      sendEvents,
      aborted,
      fmcDispatch,
    ],
  );

  const poll = useCallback(
    async (issuanceId: string): Promise<'error' | 'success' | 'timeout'> => {
      try {
        const result = await unifiedVirtualCardIssuanceStatusRequest({
          issuanceId: issuanceId,
        });

        if (stillProcessing(result)) {
          await new Promise(
            resolve =>
              (pollingRef.current = setTimeout(resolve, pollingInterval)),
          );
          return await poll(issuanceId);
        } else {
          return handleResult(result);
        }
      } catch (e) {
        return 'error';
      }
    },
    [
      pollingRef,
      pollingInterval,
      handleResult,
      unifiedVirtualCardIssuanceStatusRequest,
      stillProcessing,
    ],
  );

  const issuance = useCallback(async () => {
    try {
      // Start the abort timer. After x seconds we abort.
      abortRef.current = setTimeout(() => cleanup(), timeoutInterval);

      const result = await unifiedVirtualCardIssuanceRequest({
        applicationId: financingId,
      });

      if (stillProcessing(result)) {
        return await poll(result.issuanceId);
      } else {
        return handleResult(result);
      }
    } catch (e) {
      return 'error';
    }
  }, [
    handleResult,
    financingId,
    unifiedVirtualCardIssuanceRequest,
    poll,
    timeoutInterval,
    stillProcessing,
    cleanup,
  ]);

  return issuance;
};

export default useIssuance;
